// 全站搜尋引擎（純 JS，瀏覽器與 node 共用）。MiniSearch＋自寫 CJK 切詞（tokenizer.mjs）。
// 文件格式見 docs/SEARCH.md：{i,k,t,d,u,img,y,l,kw[],x?}。
import MiniSearch from '../vendor/minisearch.mjs'
import { tokenize, tokenizeLoose, normalize, squash, CJK_TEST } from './tokenizer.mjs'
import { KIND_WEIGHT } from './kinds.mjs'

export const FIELD_BOOST = { t: 8, alt: 4, kw: 5, d: 3, x: 1 }
const HEAD_FIELDS = ['t', 'alt', 'kw', 'd']   // 主分數的欄位；x（全文）只當次要訊號
export const X_WEIGHT = 6                      // 全文分數取 log 再乘這個權重，詞在內文出現再多次也贏不過標題
const STOPWORDS = new Set(['the', 'a', 'an', 'of', 'and', 'in', 'to', 'for', 'is', 'on', 'at'])
const LATIN_TERM = /^[a-z0-9]+$/
const MAX_QUERY = 200
const LANG_OFF_PREFERENCE = 0.92

export const escapeHtml = text => String(text ?? '').replace(/[&<>"'`]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;', '`': '&#96;' })[ch])
const escapeRegExp = text => text.replace(/[.*+?^${}()|[\]\\\-\/]/g, '\\$&')

// 命中詞：整段查詢詞，加上 CJK 段的 bigram（整段找不到時退而求其次）；長的在前，避免短詞先吃掉長詞
function needlesFor(q) {
  const found = new Set()
  for (const source of [normalize(q), String(q).toLowerCase()]) {
    for (const part of source.split(/[^\p{L}\p{N}]+/u).filter(Boolean)) {
      found.add(part)
      if (CJK_TEST.test(part)) {
        const chars = Array.from(part)
        for (let i = 0; i + 1 < chars.length; i++) found.add(chars[i] + chars[i + 1])
        if (chars.length === 1) found.add(part)
      }
    }
  }
  return [...found].filter(n => !STOPWORDS.has(n)).sort((a, b) => b.length - a.length)
}
const needleRegExp = needles => needles.length ? new RegExp(needles.map(escapeRegExp).join('|'), 'giu') : null

// 每一段先 escape 再包 <mark>：輸出只可能出現我們自己放的 <mark>，沒有可注入的 HTML。
export function highlightHtml(text, q) {
  text = String(text ?? '')
  const re = needleRegExp(needlesFor(q))
  if (!re) return escapeHtml(text)
  let out = '', last = 0
  for (const m of text.matchAll(re)) {
    if (!m[0]) continue
    out += escapeHtml(text.slice(last, m.index)) + '<mark>' + escapeHtml(m[0]) + '</mark>'
    last = m.index + m[0].length
  }
  return out + escapeHtml(text.slice(last))
}

const isLow = code => code >= 0xdc00 && code <= 0xdfff
function windowAround(text, index, span = 90, lead = 28) {
  let start = Math.max(0, index - lead)
  let end = Math.min(text.length, start + span)
  if (start > 0 && isLow(text.charCodeAt(start))) start++
  if (end < text.length && isLow(text.charCodeAt(end))) end--
  return (start > 0 ? '…' : '') + text.slice(start, end) + (end < text.length ? '…' : '')
}

export function makeSnippet(doc, q, span = 90) {
  const re = needleRegExp(needlesFor(q))
  const probe = text => { if (!re || !text) return -1; re.lastIndex = 0; const m = re.exec(text); return m ? m.index : -1 }
  for (const text of [doc.d, doc.x]) {
    const at = probe(text)
    if (at >= 0) return highlightHtml(windowAround(text, at, span), q)
  }
  const fallback = doc.d || doc.x || ''
  return escapeHtml(fallback.length > span ? fallback.slice(0, span) + '…' : fallback)
}

// 標題的比對鍵：full＝整句（去掉結尾的（YYYY））；names＝《X》裡的名字，以及冒號、破折號、括號前的主名
const BRACKET_NAME = /[《「『“"]([^》」』”"]+)[》」』”"]/u
// 2026-10-18 起中文頁標題不再用《》包名字，寫成「名字：類型 年份」（展覽、單曲、TD、草圖、地層、作品…，scripts/lib/route-titles.cjs 的 zhTitle）。
// 這個形狀（冒號或「・」之後、結尾是年份或年份區間）裡，冒號前面就是作品名，照舊算「作品名相等」；文章標題不以年份結尾，仍只算主名。
const TYPED_NAME = /^(.+?)[：・].*\s(?:19|20)\d{2}(?:[–-](?:19|20)\d{2})?$/u
const HEAD_SPLIT = /\s*[:：—–|｜(（・]\s*/u
const nameCache = new Map()
function nameKeys(title) {
  title = String(title ?? '')
  let hit = nameCache.get(title)
  if (!hit) {
    const noYear = title.replace(/\s*[（(]\s*(?:19|20)\d{2}\s*[)）]\s*$/u, '')
    const full = new Set([squash(title), squash(noYear)])
    const names = new Set(), heads = new Set()
    const m = noYear.match(BRACKET_NAME)
    if (m) names.add(squash(m[1]))
    const typed = noYear.match(TYPED_NAME)
    if (typed) names.add(squash(typed[1]))
    heads.add(squash(noYear.split(HEAD_SPLIT)[0]))
    full.delete(''); names.delete(''); heads.delete('')
    for (const f of full) { names.delete(f); heads.delete(f) }
    for (const n of names) heads.delete(n)
    hit = { full, names, heads }
    if (nameCache.size > 20000) nameCache.clear()
    nameCache.set(title, hit)
  }
  return hit
}

export function createEngine(docs) {
  const byId = new Map()
  const list = []
  for (const doc of docs) { if (doc && doc.i && !byId.has(doc.i)) { byId.set(doc.i, doc); list.push(doc) } }
  const ms = new MiniSearch({
    idField: 'i',
    fields: ['t', 'alt', 'kw', 'd', 'x'],
    storeFields: ['k'],
    tokenize,
    processTerm: term => (STOPWORDS.has(term) ? null : term),
    extractField: (doc, field) => {
      if (field === 'kw') return (doc.kw || []).join(' ')
      if (field === 't') { // 「Soul Fish」也要搜得到「soulfish」：把標題裡的拉丁詞黏成一個詞一起索引
        const words = String(doc.t || '').match(/[\p{L}\p{N}]+/gu)?.filter(w => !CJK_TEST.test(w)) || []
        return words.length > 1 ? doc.t + ' ' + words.join('') : doc.t
      }
      return doc[field]
    },
    searchOptions: { boost: FIELD_BOOST },
  })
  ms.addAll(list)

  const baseOptions = {
    boost: FIELD_BOOST,
    prefix: (term, index, terms) => LATIN_TERM.test(term) && (term.length >= 3 || (index === terms.length - 1 && term.length >= 2)),
    fuzzy: term => (LATIN_TERM.test(term) && term.length >= 4 ? 0.15 : false),
  }

  function run(q, { kinds, loose, fields, combine }) {
    const options = { ...baseOptions, combineWith: combine || (loose ? 'OR' : 'AND') }
    if (fields) options.fields = fields
    if (loose) options.tokenize = tokenizeLoose
    if (kinds?.length) { const set = new Set(kinds); options.filter = r => set.has(r.k) }
    return ms.search(q, options)
  }

  // 查詢詞（以空白與標點切開，CJK 整段算一個）是否全部出現在標題裡
  const queryWords = q => normalize(q).split(/[^\p{L}\p{N}]+/u).filter(w => w && !STOPWORDS.has(w))

  // rank：只排序，不產生 snippet（浮層與完整頁要先知道總筆數與各 kind 筆數）。
  // 命中集合＝所有欄位 AND（查無結果再 OR）；分數＝標題／別名／關鍵詞／摘要的主分數＋全文的 log 分數，再乘 kind 先驗。
  function rank(q, { kinds, lang } = {}) {
    q = String(q ?? '').trim().slice(0, MAX_QUERY)
    if (!squash(q)) return []
    let raw = run(q, { kinds }), partial = false
    if (!raw.length) { raw = run(q, { kinds, loose: true }); partial = raw.length > 0 }
    if (!raw.length) return []
    const loose = partial
    const head = new Map(run(q, { kinds, loose, fields: HEAD_FIELDS, combine: 'OR' }).map(r => [r.id, r.score]))
    const body = new Map(run(q, { kinds, loose, fields: ['x'], combine: 'OR' }).map(r => [r.id, r.score]))
    const target = squash(q), words = queryWords(q)
    return raw.map(r => {
      const doc = byId.get(r.id)
      const { full, names, heads } = nameKeys(doc.t)
      const altTitle = doc.alt ? doc.alt.split('\n')[0] : ''
      const aliases = [...(doc.kw || []), altTitle].filter(Boolean)
      // 取對數壓縮：欄位命中再多次，也不會讓 kind 先驗（作品 3.0／實驗案件 0.7）失效
      let score = Math.log1p((head.get(r.id) || 0) + X_WEIGHT * Math.log1p(body.get(r.id) || 0))
      let exact = true
      if (full.has(target)) score *= 4                     // 完整標題相等（年份括號不算）：大分
      else if (aliases.some(k => nameKeys(k).full.has(target))) score *= 4   // 另一語標題或別名整句相等
      else if (names.has(target) || aliases.some(k => nameKeys(k).names.has(target))) score *= 3   // 作品名相等（《X》裡的名字）
      else if (heads.has(target)) score *= 2               // 冒號、破折號前的主名（文章標題常以主題起頭，給得比作品名少）
      else {
        exact = false
        const title = normalize(doc.t)
        if (words.length && words.every(w => title.includes(w))) score *= 1.5        // 查詢詞全部出現在標題
        else if (words.length && words.every(w => title.includes(w) || normalize(altTitle).includes(w))) score *= 1.25
      }
      if (!exact && squash(doc.t).startsWith(target)) score *= 1.15
      score *= (KIND_WEIGHT[doc.k] ?? 1) * (lang && doc.l !== lang ? LANG_OFF_PREFERENCE : 1)
      return { doc, score, match: r.match, partial }
    }).sort((a, b) => b.score - a.score)
  }

  // 補上標題 mark 與 snippet（每段先 escape 再包 mark）
  const decorate = (results, q) => results.map(r => ({ ...r, titleHtml: highlightHtml(r.doc.t, q), snippet: makeSnippet(r.doc, q) }))

  function search(q, { kinds, limit = 50, lang } = {}) {
    return decorate(rank(q, { kinds, lang }).slice(0, limit), q)
  }

  // 查無結果時建議較短的詞：拆字、CJK 取前後兩字，只留真的有結果的
  function suggest(q, max = 4) {
    q = String(q ?? '').trim().slice(0, MAX_QUERY)
    const candidates = new Set()
    for (const part of normalize(q).split(/[^\p{L}\p{N}]+/u).filter(Boolean)) {
      if (STOPWORDS.has(part)) continue
      candidates.add(part)
      const chars = Array.from(part)
      if (CJK_TEST.test(part) && chars.length >= 3) {
        candidates.add(chars.slice(0, 2).join('')); candidates.add(chars.slice(-2).join(''))
        for (let i = 0; i + 1 < chars.length; i++) candidates.add(chars[i] + chars[i + 1])
      } else if (!CJK_TEST.test(part) && part.length >= 6) candidates.add(part.slice(0, Math.ceil(part.length * 0.7)))
    }
    candidates.delete(normalize(q))
    const out = []
    for (const word of candidates) {
      const n = run(word, {}).length
      if (n) out.push({ word, n })
    }
    return out.sort((a, b) => b.n - a.n).slice(0, max).map(x => x.word)
  }

  return { search, rank, decorate, suggest, docs: list, byId, size: list.length }
}

// 依 kind 分組：每組最多 perGroup 筆，組依該組最高分排序
export function groupResults(results, { perGroup = 4 } = {}) {
  const groups = new Map()
  for (const r of results) {
    const g = groups.get(r.doc.k) || { kind: r.doc.k, items: [], total: 0, top: r.score }
    g.total++
    if (g.items.length < perGroup) g.items.push(r)
    groups.set(r.doc.k, g)
  }
  return [...groups.values()].sort((a, b) => b.top - a.top)
}

export function countByKind(results) {
  const counts = {}
  for (const r of results) counts[r.doc.k] = (counts[r.doc.k] || 0) + 1
  return counts
}
